<template>
  <li
    class="group relative bottom-0 z-10 min-h-[80px] rounded-bl-xl before:absolute before:-top-5 before:h-5 before:w-[200px] before:bg-transparent before:content-[''] hover:z-20 md:min-h-[64px] md:rounded"
  >
    <div
      v-ui-test-name="'selectedTicketsListItem'"
      :class="[
        'justify-center after:absolute after:-right-3 after:-top-1 after:h-full after:w-3 after:bg-transparent after:content-[\'\']',
        'w-[200px]',
        'absolute bottom-0 box-border flex h-full  flex-col text-ellipsis rounded-bl-xl border border-l-0 border-neutral-300 bg-white py-3 pl-7 pr-5 transition-all',
        'group-hover:h-[120%] group-hover:justify-start',
        'xl:w-[160px] lg:pl-5 tablet:w-[140px] md:relative md:w-[133px] md:rounded md:py-2 md:pl-4 md:pr-6 md:group-hover:h-full',
      ]"
    >
      <template v-if="showGroupedTicket">
        <p
          class="mb-1.5 flex flex-col whitespace-nowrap text-sm font-medium leading-4"
        >
          <span
            v-hint="item.sectorName"
            class="mb-1 overflow-hidden text-ellipsis"
          >
            {{ item.sectorName }}
          </span>
          <span
            v-if="item.ticketsCount"
            v-hint="toPlural(item.ticketsCount, 'билет', 'билета', 'билетов')"
            class="overflow-hidden text-ellipsis font-normal leading-4 text-neutral-500"
          >
            {{ toPlural(item.ticketsCount, 'билет', 'билета', 'билетов') }}
          </span>
        </p>
      </template>
      <template v-else>
        <p
          class="mb-1.5 flex flex-col whitespace-nowrap text-sm font-medium leading-4"
        >
          <template v-if="isTicketToSeatSector">
            <span
              v-hint="getTicketDescription(item)"
              class="mb-1 overflow-hidden text-ellipsis"
            >
              {{ getTicketDescription(item) }}
            </span>
            <span
              v-hint="
                `${item.sectorName.toLowerCase()}/${item.tariffName.toLowerCase()}`
              "
              class="overflow-hidden text-ellipsis font-normal text-neutral-500"
            >
              {{ item.sectorName.toLowerCase() }}/{{
                item.tariffName.toLowerCase()
              }}.
            </span>
          </template>
          <template v-else>
            <span
              v-hint="item.sectorName"
              class="mb-1 overflow-hidden text-ellipsis"
            >
              {{ item.sectorName }}
            </span>
            <span
              v-hint="item.tariffName.toLowerCase()"
              class="overflow-hidden text-ellipsis font-normal leading-4 text-neutral-500"
            >
              {{ item.tariffName.toLowerCase() }}
            </span>
          </template>
        </p>
      </template>
      <p class="text-base font-medium leading-4">
        {{ itemPrice }}
      </p>
      <div class="absolute -right-2 -top-2 z-20 md:right-1.5 md:top-1.5">
        <UiButton
          icon
          aria-label="Удалить билет"
          class="invisible relative z-20 rounded-full bg-neutral-100 !p-0.5 group-hover:visible md:visible md:text-neutral-500"
          @click="emit('delete-ticket')"
        >
          <UiIcon name="outline/x" class="size-4 md:size-3.5" />
        </UiButton>
      </div>
      <div
        class="absolute left-0 top-0 h-full w-[15px] overflow-hidden rounded-bl-xl border-l border-l-neutral-200 md:w-[8px] md:rounded-l"
      >
        <div
          class="h-full"
          :style="{
            background: ticketColor,
          }"
        >
          <UiIcon
            name="img/ticket-color-mask"
            class="ticket__mask absolute left-0 top-0 z-0 h-[120px] w-[15px] text-transparent md:left-[-6px] md:top-[-3px]"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  </li>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import type { TicketReduced } from '@/models'

const props = defineProps({
  item: {
    required: true,
    type: Object as PropType<TicketReduced>,
  },
})

const emit = defineEmits<{
  (e: 'delete-ticket'): void
}>()

const { getTicketDescription } = useTicketStore()

const itemPrice = computed(() => {
  if (props.item.ticketsCount) return toRub(props.item.groupTicketsPrice!)

  return toRub(props.item.price)
})

/**
 * Флаг сектора с сидячими местами
 */
const isTicketToSeatSector = computed(() => {
  return !!props.item.seatId
})

const showGroupedTicket = computed(
  (): boolean => !!props.item.ticketsCount && props.item.ticketsCount! > 1,
)

const getTicketColor = computed(() => {
  const { red, green, blue, alpha } = props.item.color

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
})

const getGroupedGradient = computed(() => {
  const colors = props.item?.colors?.map(
    (item) => `rgb(${item.red},${item.green},${item.blue})`,
  )

  return colors
    ? `linear-gradient(to top, ${colors.join(',') || ''}, transparent 500%)`
    : ''
})

const ticketColor = computed(() =>
  showGroupedTicket.value ? getGroupedGradient.value : getTicketColor.value,
)
</script>
